import "./ExpiryDate.css";

import {
  sendToControllerIframe,
  sendToElementIframe,
  isExpiryDateValid,
  formatExpDate,
} from "../../utils/";
import { handleCustomInputStyle } from "../../utils/stylesheet";
import {
  VALIDATION_STATUSES,
  ELEMENTS,
  EVENTS,
  ELEMENT_FRAMES,
  FRAMES_MODES,
} from "../../constants/";
import {
  getNewValidationStatus,
  onMessage,
  registerFocusHandler,
  setOnMouseOverClasses,
  setOnMouseOutClasses,
  setOnBlurClasses,
  setOnFocusClasses,
  fixIE11InputCursor,
  wrapOnInputForIE11,
  wrapOnInputForCompositionText,
  onHiddenInputChange,
  onAutofillInput,
  simulateAutofill,
  onBackwardNavInputFocus,
  onForwardNavInputFocus,
  onIframeWindowFocus,
  onIframeWindowBlur,
  parseAndStyleEnvironment,
  setInputManualUseOnly,
  setAriaInvalidAttribute,
} from "../shared";
import { hasValue } from "../../utils/validation";

class ExpiryDate {
  state = {
    validationStatus: VALIDATION_STATUSES.EMPTY,
    value: "",
    copyPasteDisabled: false,
    cvvHidden: false,
    autoFocusDisabled: false,
  };
  previousState;
  input;

  constructor(env) {
    this.input = document.getElementById("checkout-frames-expiry-date");

    const { MODES } = parseAndStyleEnvironment(env);
    this.state.copyPasteDisabled = hasValue(
      MODES,
      FRAMES_MODES.DISABLE_COPY_PASTE
    );
    this.state.cvvHidden = hasValue(MODES, FRAMES_MODES.CVV_HIDDEN);
    this.state.autoFocusDisabled = hasValue(
      MODES,
      FRAMES_MODES.DISABLE_AUTO_FOCUS
    );

    if (hasValue(MODES, FRAMES_MODES.RIGHT_TO_LEFT)) {
      this.input.parentNode.setAttribute("dir", "rtl");
    }

    this.registerEventHandlers();
    this.previousState = { ...this.state };

    sendToControllerIframe(ELEMENTS.EXPIRY_DATE, {
      event: EVENTS.FRAME_ACTIVATED,
    });
  }

  registerEventHandlers() {
    this.input.addEventListener("mouseover", this.onMouseOver);
    this.input.addEventListener("mouseout", this.onMouseOut);
    registerFocusHandler(this.input, this.onFocus);
    this.input.addEventListener(
      "input",
      wrapOnInputForIE11(wrapOnInputForCompositionText(this.onInputChange))
    );
    this.input.addEventListener("compositionend", this.onInputChange);
    this.input.addEventListener("blur", this.onBlur);

    if (this.copyPasteDisabled) {
      setInputManualUseOnly(this.input);
    }

    document
      .getElementById("cc-number-hidden")
      .addEventListener("input", onHiddenInputChange);
    document
      .getElementById("cc-csc-hidden")
      .addEventListener("input", onHiddenInputChange);

    window.addEventListener(
      "message",
      onMessage("expiryDate", this.onReceiveMessage)
    );

    this.backwardNavInput = document.getElementById("backward-nav-input");
    if (this.backwardNavInput) {
      this.backwardNavInput.addEventListener(
        "focus",
        onBackwardNavInputFocus(ELEMENT_FRAMES.EXPIRY_DATE)
      );
    }

    this.forwardNavInput = document.getElementById("forward-nav-input");
    if (this.forwardNavInput) {
      this.forwardNavInput.addEventListener(
        "focus",
        onForwardNavInputFocus(ELEMENT_FRAMES.EXPIRY_DATE)
      );
    }

    window.addEventListener(
      "focus",
      onIframeWindowFocus(
        this.input,
        this.backwardNavInput,
        this.forwardNavInput
      )
    );
    window.addEventListener(
      "blur",
      onIframeWindowBlur(this.backwardNavInput, this.forwardNavInput)
    );
  }

  updateValidationStatus = () => {
    this.previousState.validationStatus = this.state.validationStatus;

    this.state.validationStatus = getNewValidationStatus(
      this.input.value,
      isExpiryDateValid,
      this.state.value
    );
  };

  /**
   * Expiry Date change handler
   * Format input & update the state (value, validity)
   */
  onInputChange = (event) => {
    const oldValue = this.state.value;
    const newValue = event.target.value;

    const formattedValue = formatExpDate(oldValue, newValue);

    this.state.value = formattedValue;
    this.input.value = formattedValue;

    this.updateValidationStatus();

    onAutofillInput(event);

    if (
      !this.state.autoFocusDisabled &&
      !this.state.cvvHidden &&
      this.state.value.length === 5
    ) {
      sendToElementIframe(ELEMENT_FRAMES.CVV, { message: "forceFocus" });
    }

    sendToControllerIframe(ELEMENTS.EXPIRY_DATE, {
      event: EVENTS.VALUE_CHANGED,
      value: this.state.value,
      validationStatus: this.state.validationStatus,
      previousValidationStatus: this.previousState.validationStatus,
    });

    fixIE11InputCursor(this.input);
  };

  onMouseOver = () => {
    setOnMouseOverClasses(this.input, "expiry-date");
  };

  onMouseOut = () => {
    setOnMouseOutClasses(this.input, "expiry-date");
  };

  /**
   * Handler when expiry date input obtains focus
   */
  onFocus = () => {
    sendToControllerIframe(ELEMENTS.EXPIRY_DATE, {
      event: EVENTS.FRAME_FOCUS,
      validationStatus: this.state.validationStatus,
    });

    setOnFocusClasses(this.input, "expiry-date");
  };

  /**
   * Handler when the expiry date input loses focus
   */
  onBlur = () => {
    sendToControllerIframe(ELEMENTS.EXPIRY_DATE, {
      event: EVENTS.FRAME_BLUR,
      validationStatus: this.state.validationStatus,
    });
    setAriaInvalidAttribute(this.input, this.state.validationStatus);
    setOnBlurClasses(this.input, "expiry-date", this.state);
  };

  onReceiveMessage = (e) => {
    const { message, style, value } = e.data.data;
    switch (message) {
      case "initial-config":
        if (style)
          handleCustomInputStyle({ style, element: ELEMENTS.EXPIRY_DATE });
        break;
      case "clear":
        this.state.value = "";
        break;
      case "forceFocus":
        this.input.focus();
        break;
      case "forceBlur":
        this.onBlur();
        break;
      case "autofilled":
        simulateAutofill(this.input, value);
        break;
      default:
        break;
    }
  };
}

export default ExpiryDate;
